import { DOCS_URL, NEW_ISSUE_URL, REPO_URLS } from '../../common/constants';
import OclOnlineDiagram from '../../components/tools/OclOnlineDiagram';

// Copy from the Webflow tool detail pages (tools/*.html), translated via
// tools_config.* in the locale bundles. Non-translatable wiring (app/href
// markers, primary flags, doc links) stays here and is zipped with the
// translated button labels by index.
//
// Testimonials are the exception: they live in the shared, page-neutral
// `testimonials.*` namespace rather than under tools_config, because the home
// ticker (components/home/Testimonials.jsx) shows the same quotes. Keeping
// them under a tool meant the home page depended on a tool page's optional
// data, and it broke the moment /tools/mapper dropped its testimonial
// (ocl_online#182). Each tool opts in explicitly below, so which pages carry
// a quote stays a decision here and not a side effect of the content file.
const BUTTON_LINKS = {
  termbrowser: [
    { app: 'termBrowser', primary: true },
    { href: REPO_URLS.termbrowser },
    { href: NEW_ISSUE_URL },
  ],
  'terminology-service': [
    { app: 'termBrowser', primary: true },
    { app: 'swagger' },
    { href: DOCS_URL },
    { href: REPO_URLS['terminology-service'] },
    { href: NEW_ISSUE_URL },
  ],
  'ocl-online': [
    { app: 'termBrowser', primary: true },
  ],
  mapper: [
    { app: 'mapper', primary: true },
    { href: REPO_URLS.mapper },
    { href: NEW_ISSUE_URL },
  ],
};

// Screenshot paths for tools that render a ProductShowcase instead of the
// generic featureList. Bare viewport captures at 1.4:1 (1400 × 1000 CSS px at
// 2×) — no browser chrome and no baked shadow; the component draws the border
// and elevation.
const SHOWCASE_IMAGES = {
  termbrowser: '/images/tools/termbrowser-showcase.png',
  'terminology-service': '/images/tools/terminology-service-showcase.png',
  mapper: '/images/tools/mapper-showcase.png',
};

// `jsonKey` is the locale-bundle namespace (underscored); `linksKey` is the
// TOOLS/BUTTON_LINKS key (hyphenated, matching the /tools/:slug route).
const getStarted = (t, jsonKey, linksKey) => {
  const config = t(`tools_config.${jsonKey}.getStarted`, { returnObjects: true });
  return {
    ...config,
    buttons: config.buttons.map((button, i) => ({ ...button, ...BUTTON_LINKS[linksKey][i] })),
  };
};

export const getTools = t => ({
  'termbrowser': {
    ...t('tools_config.termbrowser', { returnObjects: true }),
    // No quote: the Webflow export only had lorem ipsum for this one.
    testimonial: null,
    showcase: {
      ...t('tools_config.termbrowser.showcase', { returnObjects: true }),
      image: SHOWCASE_IMAGES.termbrowser,
    },
    getStarted: getStarted(t, 'termbrowser', 'termbrowser'),
  },
  'terminology-service': {
    ...t('tools_config.terminology_service', { returnObjects: true }),
    // This page dropped Beatriz de Faria Leão's quote with its feature list
    // (ocl_online#175), and the home ticker dropped it too (ocl_online#239).
    testimonial: null,
    showcase: {
      ...t('tools_config.terminology_service.showcase', { returnObjects: true }),
      image: SHOWCASE_IMAGES['terminology-service'],
    },
    getStarted: getStarted(t, 'terminology_service', 'terminology-service'),
  },
  'ocl-online': {
    ...t('tools_config.ocl_online', { returnObjects: true }),
    // Ellen Ball's quote runs in the home ticker only; this page shows how
    // OCL Online fits together in its place (ocl_online#175).
    testimonial: null,
    // A diagram, not a screenshot: OCL Online is the service around the
    // tools, so its showcase is drawn rather than captured.
    showcase: {
      ...t('tools_config.ocl_online.showcase', { returnObjects: true }),
      media: OclOnlineDiagram,
    },
    organizations: {
      ...t('tools_config.ocl_online.organizations', { returnObjects: true }),
      link: { label: t('tools_config.ocl_online.organizations.link_label'), href: DOCS_URL },
    },
    getStarted: getStarted(t, 'ocl_online', 'ocl-online'),
  },
  'mapper': {
    ...t('tools_config.mapper', { returnObjects: true }),
    // Derek Treatman's quote runs in the home ticker only — this page shows
    // the product showcase in its place (ocl_online#129).
    testimonial: null,
    showcase: {
      ...t('tools_config.mapper.showcase', { returnObjects: true }),
      image: SHOWCASE_IMAGES.mapper,
    },
    getStarted: getStarted(t, 'mapper', 'mapper'),
  },
});
